import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  ArrowBack,
  Edit,
  Business,
  Email,
  Phone,
  LocationOn,
  CalendarToday,
  Description,
  Assessment,
  Language,
  Work,
} from '@mui/icons-material';
import api from '../utils/axios';
import dayjs from 'dayjs';

interface Company {
  id: string;
  name: string;
  type: string;
  industry?: string;
  website?: string;
  phone?: string;
  email?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  contacts?: Contact[];
  claims?: Claim[];
}

interface Contact {
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  title?: string;
  department?: string;
  address?: string;
  createdAt: string;
}

interface Claim {
  id: string;
  claimNumber: string;
  status: string;
  type: string;
  amount?: number;
  description?: string;
  dateOfLoss?: string;
  dateReported?: string;
  createdAt: string;
}

const CompanyDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState<Partial<Company>>({});

  const { data: companyData, isLoading, error, refetch } = useQuery({
    queryKey: ['company', id],
    queryFn: async (): Promise<{ success: boolean; data: Company }> => {
      const response = await api.get<{ success: boolean; data: Company }>(`/companies/${id}`);
      return response.data;
    },
    enabled: !!id,
  });

  const company = companyData?.data;

  useEffect(() => {
    if (company) {
      setEditForm({
        name: company.name,
        type: company.type,
        industry: company.industry,
        website: company.website,
        phone: company.phone,
        email: company.email,
        address: company.address,
        city: company.city,
        state: company.state,
        zipCode: company.zipCode,
        country: company.country,
        status: company.status,
      });
    }
  }, [company]);

  const handleInputChange = (field: keyof Company, value: string) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      await api.put(`/companies/${id}`, editForm);
      setIsEditDialogOpen(false);
      refetch();
    } catch (error) {
      console.error('Error updating company:', error);
    }
  };

  const handleCancel = () => {
    setIsEditDialogOpen(false);
    if (company) {
      setEditForm({
        name: company.name,
        type: company.type,
        industry: company.industry,
        website: company.website,
        phone: company.phone,
        email: company.email,
        address: company.address,
        city: company.city,
        state: company.state,
        zipCode: company.zipCode,
        country: company.country,
        status: company.status,
      });
    }
  };

  const getCompanyTypeColor = (type: string) => {
    switch (type) {
      case 'INSURANCE_COMPANY':
        return 'primary';
      case 'BUSINESS':
        return 'secondary';
      case 'VENDOR':
        return 'warning';
      case 'SUPPLIER':
        return 'info';
      default:
        return 'default';
    }
  };

  const getCompanyTypeLabel = (type: string) => {
    switch (type) {
      case 'INSURANCE_COMPANY':
        return 'Insurance Company';
      case 'BUSINESS':
        return 'Business';
      case 'VENDOR':
        return 'Vendor';
      case 'SUPPLIER':
        return 'Supplier';
      default:
        return type;
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !company) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Failed to load company details. Please try again.
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton onClick={() => navigate('/companies')} sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" component="h1">
          {company.name}
        </Typography>
        <Box ml="auto">
          <Button
            variant="outlined"
            startIcon={<Edit />}
            onClick={() => setIsEditDialogOpen(true)}
          >
            Edit Company
          </Button>
        </Box>
      </Box>

      <Box display="flex" flexDirection="column" gap={3}>
        <Box display="flex" gap={3} flexWrap="wrap">
          {/* Basic Information */}
          <Box flex="1" minWidth="300px">
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <Business sx={{ mr: 1 }} />
                  <Typography variant="h6">Company Information</Typography>
                </Box>
                
                <Box display="flex" flexDirection="column" gap={2}>
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">
                      Company Name
                    </Typography>
                    <Typography variant="body1">{company.name}</Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">
                      Company Type
                    </Typography>
                    <Chip 
                      label={getCompanyTypeLabel(company.type)} 
                      color={getCompanyTypeColor(company.type) as any}
                      size="small"
                    />
                  </Box>
                  
                  {company.industry && (
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">
                        Industry
                      </Typography>
                      <Box display="flex" alignItems="center">
                        <Work sx={{ mr: 1, fontSize: 16 }} />
                        <Typography variant="body1">{company.industry}</Typography>
                      </Box>
                    </Box>
                  )}
                  
                  {company.website && (
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">
                        Website
                      </Typography>
                      <Box display="flex" alignItems="center">
                        <Language sx={{ mr: 1, fontSize: 16 }} />
                        <Typography variant="body1">{company.website}</Typography>
                      </Box>
                    </Box>
                  )}
                  
                  {company.email && (
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">
                        Email
                      </Typography>
                      <Box display="flex" alignItems="center">
                        <Email sx={{ mr: 1, fontSize: 16 }} />
                        <Typography variant="body1">{company.email}</Typography>
                      </Box>
                    </Box>
                  )}
                  
                  {company.phone && (
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">
                        Phone
                      </Typography>
                      <Box display="flex" alignItems="center">
                        <Phone sx={{ mr: 1, fontSize: 16 }} />
                        <Typography variant="body1">{company.phone}</Typography>
                      </Box>
                    </Box>
                  )}
                  
                  {(company.address || company.city || company.state || company.zipCode) && (
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">
                        Address
                      </Typography>
                      <Box display="flex" alignItems="center">
                        <LocationOn sx={{ mr: 1, fontSize: 16 }} />
                        <Typography variant="body1">
                          {[company.address, company.city, company.state, company.zipCode]
                            .filter(Boolean)
                            .join(', ')}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Box>

          {/* Statistics */}
          <Box flex="1" minWidth="300px">
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <Assessment sx={{ mr: 1 }} />
                  <Typography variant="h6">Statistics</Typography>
                </Box>
                
                <Box display="flex" gap={2}>
                  <Box flex="1" textAlign="center">
                    <Typography variant="h4" color="primary">
                      {company.contacts?.length || 0}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Total Contacts
                    </Typography>
                  </Box>
                  <Box flex="1" textAlign="center">
                    <Typography variant="h4" color="secondary">
                      {company.claims?.length || 0}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Claims
                    </Typography>
                  </Box>
                </Box>
                
                <Divider sx={{ my: 2 }} />
                
                <Box>
                  <Typography variant="subtitle2" color="textSecondary">
                    Status
                  </Typography>
                  <Chip 
                    label={company.status} 
                    color={company.status === 'ACTIVE' ? 'success' : 'error'}
                    size="small"
                  />
                </Box>
                
                <Box mt={2}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Created
                  </Typography>
                  <Box display="flex" alignItems="center">
                    <CalendarToday sx={{ mr: 1, fontSize: 16 }} />
                    <Typography variant="body2">
                      {dayjs(company.createdAt).format('MMM DD, YYYY')}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>

        {/* Contacts */}
        <Box>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <Description sx={{ mr: 1 }} />
                <Typography variant="h6">Contacts</Typography>
              </Box>
              
              {company.contacts && company.contacts.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Title</TableCell>
                        <TableCell>Department</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Phone</TableCell>
                        <TableCell>Address</TableCell>
                        <TableCell>Created</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {company.contacts.map((contact) => (
                        <TableRow key={contact.id}>
                          <TableCell>
                            <Button
                              variant="text"
                              onClick={() => navigate(`/contacts/${contact.id}`)}
                            >
                              {contact.firstName} {contact.lastName}
                            </Button>
                          </TableCell>
                          <TableCell>{contact.title || 'N/A'}</TableCell>
                          <TableCell>{contact.department || 'N/A'}</TableCell>
                          <TableCell>{contact.email || 'N/A'}</TableCell>
                          <TableCell>{contact.phone || 'N/A'}</TableCell>
                          <TableCell>{contact.address || 'N/A'}</TableCell>
                          <TableCell>
                            {dayjs(contact.createdAt).format('MM/DD/YYYY')}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No contacts found for this company.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Claims */}
        <Box>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <Assessment sx={{ mr: 1 }} />
                <Typography variant="h6">Claims</Typography>
              </Box>
              
              {company.claims && company.claims.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Claim Number</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>Date of Loss</TableCell>
                        <TableCell>Date Reported</TableCell>
                        <TableCell>Created</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {company.claims.map((claim) => (
                        <TableRow key={claim.id}>
                          <TableCell>
                            <Button
                              variant="text"
                              onClick={() => navigate(`/claims/${claim.file_number}`)}
                            >
                              {claim.claimNumber}
                            </Button>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={claim.status}
                              color={
                                claim.status === 'ACTIVE' ? 'success' :
                                claim.status === 'PENDING' ? 'warning' : 'default'
                              }
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{claim.type}</TableCell>
                          <TableCell>
                            {claim.amount ? `$${claim.amount.toLocaleString()}` : 'N/A'}
                          </TableCell>
                          <TableCell>
                            {claim.dateOfLoss ? dayjs(claim.dateOfLoss).format('MM/DD/YYYY') : 'N/A'}
                          </TableCell>
                          <TableCell>
                            {claim.dateReported ? dayjs(claim.dateReported).format('MM/DD/YYYY') : 'N/A'}
                          </TableCell>
                          <TableCell>
                            {dayjs(claim.createdAt).format('MM/DD/YYYY')}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No claims found for this company.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onClose={handleCancel} maxWidth="md" fullWidth>
        <DialogTitle>Edit Company</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <Box display="flex" gap={2}>
              <TextField
                label="Company Name"
                value={editForm.name || ''}
                onChange={(e) => handleInputChange('name', e.target.value)}
                fullWidth
                required
              />
              <FormControl fullWidth>
                <InputLabel>Company Type</InputLabel>
                <Select
                  value={editForm.type || ''}
                  label="Company Type"
                  onChange={(e) => handleInputChange('type', e.target.value)}
                >
                  <MenuItem value="BUSINESS">Business</MenuItem>
                  <MenuItem value="VENDOR">Vendor</MenuItem>
                  <MenuItem value="SUPPLIER">Supplier</MenuItem>
                  <MenuItem value="INSURANCE_COMPANY">Insurance Company</MenuItem>
                  <MenuItem value="OTHER">Other</MenuItem>
                </Select>
              </FormControl>
            </Box>
            
            <Box display="flex" gap={2}>
              <TextField
                label="Industry"
                value={editForm.industry || ''}
                onChange={(e) => handleInputChange('industry', e.target.value)}
                fullWidth
              />
              <TextField
                label="Website"
                value={editForm.website || ''}
                onChange={(e) => handleInputChange('website', e.target.value)}
                fullWidth
              />
            </Box>
            
            <Box display="flex" gap={2}>
              <TextField
                label="Email"
                type="email"
                value={editForm.email || ''}
                onChange={(e) => handleInputChange('email', e.target.value)}
                fullWidth
              />
              <TextField
                label="Phone"
                value={editForm.phone || ''}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                fullWidth
              />
            </Box>
            
            <TextField
              label="Address"
              value={editForm.address || ''}
              onChange={(e) => handleInputChange('address', e.target.value)}
              fullWidth
              multiline
              rows={2}
            />
            
            <Box display="flex" gap={2}>
              <TextField
                label="City"
                value={editForm.city || ''}
                onChange={(e) => handleInputChange('city', e.target.value)}
                fullWidth
              />
              <TextField
                label="State"
                value={editForm.state || ''}
                onChange={(e) => handleInputChange('state', e.target.value)}
                fullWidth
              />
              <TextField
                label="ZIP Code"
                value={editForm.zipCode || ''}
                onChange={(e) => handleInputChange('zipCode', e.target.value)}
                fullWidth
              />
            </Box>
            
            <TextField
              label="Country"
              value={editForm.country || ''}
              onChange={(e) => handleInputChange('country', e.target.value)}
              fullWidth
            />
            
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={editForm.status || ''}
                label="Status"
                onChange={(e) => handleInputChange('status', e.target.value)}
              >
                <MenuItem value="ACTIVE">Active</MenuItem>
                <MenuItem value="INACTIVE">Inactive</MenuItem>
                <MenuItem value="PENDING">Pending</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CompanyDetails; 